import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Phone, Users, Calendar, Clock, TrendingUp, Sparkles, RefreshCw, UtensilsCrossed, Stethoscope, HardHat, Scissors, Scale, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import { GlassCard, CardLabel } from './GlassCard';
import { LiveCallCard } from './LiveCallCard';
import { useLocations } from '../../context/LocationContext';

const HOME_WIDGETS = {
  restaurant: { bookingStatLabel: "Today's Covers", bookingStatIcon: "UtensilsCrossed", leadLabel: "New Inquiries" },
  clinic: { bookingStatLabel: "Today's Patients", bookingStatIcon: "Stethoscope", leadLabel: "New Patient Inquiries" },
  medical: { bookingStatLabel: "Today's Patients", bookingStatIcon: "Stethoscope", leadLabel: "New Patient Inquiries" },
  contractor: { bookingStatLabel: "Active Jobs", bookingStatIcon: "HardHat", leadLabel: "New Leads" },
  general_contractor: { bookingStatLabel: "Active Jobs", bookingStatIcon: "HardHat", leadLabel: "New Leads" },
  salon: { bookingStatLabel: "Today's Bookings", bookingStatIcon: "Scissors", leadLabel: "New Clients" },
  hair_salon: { bookingStatLabel: "Today's Bookings", bookingStatIcon: "Scissors", leadLabel: "New Clients" },
  legal: { bookingStatLabel: "Consultations Booked", bookingStatIcon: "Scale", leadLabel: "New Inquiries" },
  real_estate: { bookingStatLabel: "Showings Scheduled", bookingStatIcon: "Home", leadLabel: "Buyer/Seller Leads" },
  other: { bookingStatLabel: "Appointments", bookingStatIcon: "Calendar", leadLabel: "New Leads" },
};

const ICON_MAP = { UtensilsCrossed, Stethoscope, HardHat, Scissors, Scale, Home, Calendar };

export default function DashboardHome() {
  const { api, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [brief, setBrief] = useState('');
  const [activities, setActivities] = useState([]);
  const [briefLoading, setBriefLoading] = useState(false);
  const business = user?.business;
  const niche = user?.business?.niche || 'other';
  const widgets = HOME_WIDGETS[niche] || HOME_WIDGETS['other'];
  const BookingIcon = ICON_MAP[widgets.bookingStatIcon] || Calendar;
  const { activeLocationId, apiQueryParam } = useLocations();

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [statsRes, briefRes, feedRes] = await Promise.all([
          api.get(`/dashboard/stats${apiQueryParam()}`),
          api.get('/agents/daily-brief'),
          api.get('/agents/activity-feed'),
        ]);
        setStats(statsRes.data);
        setBrief(briefRes.data.brief || '');
        setActivities(feedRes.data.activities || []);
      } catch {}
    };
    fetchAll();
  }, [api, activeLocationId, apiQueryParam]);

  const refreshBrief = async () => {
    setBriefLoading(true);
    try {
      const { data } = await api.post('/agents/daily-brief/refresh');
      setBrief(data.brief);
    } catch {} finally { setBriefLoading(false); }
  };

  const minutesPercent = stats ? Math.round((stats.minutes_used / stats.minutes_cap) * 100) : 0;

  return (
    <div data-testid="dashboard-home" className="space-y-5 w-full">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-gray-400 dark:text-slate-500 font-medium mb-1">Overview</p>
          <h1 className="font-heading text-3xl md:text-[34px] font-semibold text-gray-900 dark:text-white tracking-tight leading-none">
            Welcome back, {user?.name?.split(' ')[0] || 'there'}
          </h1>
        </div>
        <p className="text-xs text-gray-400 dark:text-slate-500 tabular-nums">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </p>
      </div>

      {/* Trial banner */}
      {stats?.is_trial && (
        <div className="bg-amber-50/70 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-2xl p-4 flex items-start gap-3">
          <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
              Free trial active, {Math.max(0, (stats?.minutes_cap || 30) - (stats?.minutes_used || 0))} of {stats?.minutes_cap || 30} free minutes left
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300/80 mt-0.5">
              {stats?.trial_ends_at && `Trial ends ${new Date(stats.trial_ends_at).toLocaleDateString()}. `}
              <Link to="/dashboard/settings?tab=billing" className="underline hover:text-amber-900 dark:hover:text-amber-100 font-medium">Upgrade now</Link> to keep Ava live.
            </p>
          </div>
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Phone} label="Total Calls" value={stats?.calls_count ?? 0} />
        <StatCard icon={Users} label={widgets.leadLabel} value={stats?.leads_count ?? 0} />
        <StatCard icon={BookingIcon} label={widgets.bookingStatLabel} value={stats?.appointments_count ?? 0} />
        <StatCard icon={TrendingUp} label="Conversion" value={stats?.leads_count ? `${Math.round((stats.appointments_count / stats.leads_count) * 100)}%` : '0%'} />
      </div>

      {/* Main: Ava hero (left, full size) + filled right rail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
        {/* Left — Ava hero + daily brief */}
        <div className="lg:col-span-2 space-y-5">
          <LiveCallCard />

          <GlassCard className="p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-blue-50 dark:bg-blue-500/15 border border-blue-100 dark:border-blue-500/30 flex items-center justify-center">
                  <Sparkles className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="font-heading text-sm font-medium text-gray-900 dark:text-white">Daily Brief</h3>
              </div>
              <button onClick={refreshBrief} disabled={briefLoading} aria-label="Refresh brief" className="text-gray-400 dark:text-slate-500 hover:text-gray-700 dark:hover:text-white p-1 rounded-md transition-colors">
                <RefreshCw className={`w-3.5 h-3.5 ${briefLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
            <p className="text-gray-600 dark:text-slate-300 text-sm leading-relaxed">{brief || 'Loading your personalized brief...'}</p>
          </GlassCard>
        </div>

        {/* Right rail — fills the space: Ava status, quick actions, usage */}
        <div className="space-y-5">
          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading text-sm font-medium text-gray-900 dark:text-white">AI Agent: {stats?.ai_agent_name || 'Ava'}</h3>
              <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${business?.twilio_number ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300' : 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'}`}>
                {business?.twilio_number ? 'Active' : 'Setup'}
              </span>
            </div>
            <p className="text-gray-500 dark:text-slate-400 text-xs mb-4">Handling calls 24/7 on your behalf</p>
            {business?.twilio_number ? (
              <div className="bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-xl px-4 py-3 flex items-center gap-3">
                <Phone className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                <span data-testid="business-phone-number" className="font-mono text-sm font-medium text-gray-900 dark:text-white tabular-nums truncate">{business.twilio_number}</span>
                <span className="text-[11px] text-gray-400 dark:text-slate-500 ml-auto whitespace-nowrap">Call to test</span>
              </div>
            ) : (
              <Link to="/onboarding" data-testid="finish-onboarding-cta"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-full px-4 py-2 transition-colors">
                Finish setting up Ava <Sparkles className="w-3.5 h-3.5" />
              </Link>
            )}
          </GlassCard>

          <GlassCard className="p-6">
            <h3 className="font-heading text-sm font-medium text-gray-900 dark:text-white mb-4">Quick Actions</h3>
            <div className="grid grid-cols-2 gap-3">
              <QuickAction to="/dashboard/leads" icon={Users} label="View Leads" />
              <QuickAction to="/dashboard/appointments" icon={Calendar} label="Appointments" />
              <QuickAction to="/dashboard/calls" icon={Phone} label="Call Logs" />
              <QuickAction to="/dashboard/analytics" icon={TrendingUp} label="Analytics" />
            </div>
          </GlassCard>

          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                <h3 className="font-heading text-sm font-medium text-gray-900 dark:text-white">Minutes Usage</h3>
              </div>
              <span className="text-xs text-gray-500 dark:text-slate-400 tabular-nums">
                <span className="text-gray-900 dark:text-white font-medium">{stats?.minutes_used || 0}</span>
                <span className="text-gray-400 dark:text-slate-500">/{stats?.minutes_cap || 30} min</span>
              </span>
            </div>
            <div className="w-full h-1.5 bg-gray-100 dark:bg-white/[0.05] rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${minutesPercent >= 95 ? 'bg-red-500' : minutesPercent >= 80 ? 'bg-amber-500' : 'bg-blue-600'}`}
                style={{ width: `${Math.min(minutesPercent, 100)}%` }} />
            </div>
            {minutesPercent >= 80 && minutesPercent < 90 && !stats?.is_trial && (
              <Link to="/dashboard/settings?tab=billing" data-testid="add-minutes-inline-link" className="inline-block mt-3 text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium">+ Add minutes</Link>
            )}
          </GlassCard>
        </div>
      </div>

      {/* Low minutes warning (full width) */}
      {minutesPercent >= 90 && !stats?.is_trial && (
        <div data-testid="low-minutes-banner" className={`flex items-center justify-between gap-4 rounded-2xl px-5 py-4 border ${minutesPercent >= 100 ? 'bg-red-50 dark:bg-red-500/15 border-red-200 dark:border-red-500/40' : 'bg-amber-50 dark:bg-amber-500/15 border-amber-200 dark:border-amber-500/40'}`}>
          <div>
            <p className={`text-sm font-medium ${minutesPercent >= 100 ? 'text-red-800 dark:text-red-200' : 'text-amber-800 dark:text-amber-200'}`}>
              {minutesPercent >= 100 ? "You've used all your minutes" : 'Running low on minutes'}
            </p>
            <p className={`text-xs mt-0.5 ${minutesPercent >= 100 ? 'text-red-600 dark:text-red-300/90' : 'text-amber-600 dark:text-amber-300/90'}`}>
              {minutesPercent >= 100
                ? 'Ava has paused. Buy more minutes to resume taking calls.'
                : `Only ${(stats?.minutes_cap || 0) - (stats?.minutes_used || 0)} minutes remaining this cycle.`}
            </p>
          </div>
          <Link to="/dashboard/settings?tab=billing" data-testid="low-minutes-add-cta"
            className={`whitespace-nowrap text-sm font-medium rounded-full px-4 py-2 flex-shrink-0 ${minutesPercent >= 100 ? 'bg-red-600 hover:bg-red-500 text-white' : 'bg-amber-600 hover:bg-amber-500 text-white'}`}>
            Add Minutes
          </Link>
        </div>
      )}

      {/* Activity feed (full width) */}
      <GlassCard className="p-6">
        <h3 className="font-heading text-base font-medium text-gray-900 dark:text-white mb-4">Agent Activity</h3>
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {activities.length === 0 ? (
            <p className="text-gray-400 dark:text-slate-500 text-xs text-center py-8">Your AI agents will log their actions here as they work.</p>
          ) : activities.map((act, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[10px] font-bold" style={{ backgroundColor: act.color || '#2563eb' }}>
                {act.agent?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-700 dark:text-slate-200">{act.action}</p>
                {act.detail && <p className="text-[10px] text-gray-400 dark:text-slate-500 truncate">{act.detail}</p>}
                <p className="text-[10px] text-gray-300 dark:text-slate-600 mt-0.5">{new Date(act.created_at).toLocaleString()}</p>
              </div>
            </div>
          ))}
        </div>
      </GlassCard>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <GlassCard className="group p-5 hover:border-blue-300/50 dark:hover:border-blue-500/30 transition-all">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-500/15 border border-blue-100 dark:border-blue-500/20 flex items-center justify-center">
          <Icon className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
        </div>
        <CardLabel>{label}</CardLabel>
      </div>
      <p className="text-3xl font-heading font-semibold text-gray-900 dark:text-white tabular-nums tracking-tight leading-none">{value}</p>
    </GlassCard>
  );
}

function QuickAction({ to, icon: Icon, label }) {
  return (
    <Link to={to} className="flex flex-col items-center justify-center gap-2 bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.06] rounded-xl py-4 hover:border-blue-300 dark:hover:border-blue-500/40 hover:bg-blue-50/50 dark:hover:bg-blue-500/[0.06] transition-colors text-center">
      <Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
      <span className="text-xs text-gray-600 dark:text-slate-300 font-medium">{label}</span>
    </Link>
  );
}
